// @flow

import React, { PureComponent } from 'react';
import { createPaginationContainer, graphql } from 'react-relay';
import debounce from 'lodash.debounce';
import type { RouterHistory } from 'react-router';
import { BrowserUtils } from 'shared/utils';
import LoadingMore from 'shared/components/common/LoadingMore';
import EmptySearchResults from 'shared/components/common/EmptySearchResults';
import InvoicesTable from './InvoicesTable';

type Props = {
  history: RouterHistory,
  filtersDirty: boolean,
  onCreateNew: () => void,
  invoices: Object,
  deleteEnabled: boolean,
  editEnabled: boolean,
  relay: {
    environment: Object,
    hasMore: () => boolean,
    isLoading: () => boolean,
    loadMore: (pageSize: number, callback: Function) => void,
  },
};

class PaginatedInvoicesContainer extends PureComponent<Props> {
  constructor(props: Props) {
    super(props);
    this.onScrollEndReached = debounce(this.onScrollEndReached, 100);
  }

  componentDidMount = () => {
    document.addEventListener('scroll', this.onScrollEndReached);
  };

  componentWillUnmount = () => {
    document.removeEventListener('scroll', this.onScrollEndReached);
  };

  onScrollEndReached = () => {
    const { relay } = this.props;
    if (BrowserUtils.scrollEndReached(200)) {
      if (relay.hasMore()) {
        relay.loadMore(30);
      }
    }
  };

  render() {
    const {
      history,
      onCreateNew,
      invoices,
      relay,
      filtersDirty,
      editEnabled,
      deleteEnabled,
    } = this.props;

    if (
      (!invoices.invoices.edges || !invoices.invoices.edges.length) &&
      filtersDirty
    ) {
      return <EmptySearchResults />;
    }

    return (
      <>
        <InvoicesTable
          history={history}
          onCreateNew={onCreateNew}
          invoiceEdges={invoices.invoices.edges}
          deleteEnabled={deleteEnabled}
          editEnabled={editEnabled}
        />
        {relay.hasMore() && relay.isLoading() ? <LoadingMore /> : null}
      </>
    );
  }
}

export default createPaginationContainer(
  PaginatedInvoicesContainer,
  {
    invoices: graphql`
      fragment PaginatedInvoicesContainer_invoices on Query
        @argumentDefinitions(
          count: { type: "Int", defaultValue: 30 }
          cursor: { type: "String" }
          search: { type: "String" }
          sortBy: { type: "InvoiceSort" }
          statuses: { type: "[InvoiceStatus!]" }
          customerIds: { type: "[ID!]" }
          orderIds: { type: "[ID!]" }
          issueDateStart: { type: "Date" }
          issueDateEnd: { type: "Date" }
          dueDateStart: { type: "Date" }
          dueDateEnd: { type: "Date" }
        ) {
        invoices(
          first: $count
          after: $cursor
          search: $search
          sortBy: $sortBy
          statuses: $statuses
          customerIds: $customerIds
          orderIds: $orderIds
          issueDateStart: $issueDateStart
          issueDateEnd: $issueDateEnd
          dueDateStart: $dueDateStart
          dueDateEnd: $dueDateEnd
        ) @connection(key: "PaginatedInvoicesContainer_invoices", filters: []) {
          edges {
            cursor
            ...InvoicesTable_invoiceEdges
          }
        }
      }
    `,
  },
  {
    query: graphql`
      query PaginatedInvoicesContainerQuery(
        $count: Int!
        $cursor: String
        $search: String
        $sortBy: InvoiceSort
        $statuses: [InvoiceStatus!]
        $customerIds: [ID!]
        $orderIds: [ID!]
        $issueDateStart: Date
        $issueDateEnd: Date
        $dueDateStart: Date
        $dueDateEnd: Date
      ) {
        ...PaginatedInvoicesContainer_invoices
          @arguments(
            count: $count
            cursor: $cursor
            search: $search
            sortBy: $sortBy
            statuses: $statuses
            customerIds: $customerIds
            orderIds: $orderIds
            issueDateStart: $issueDateStart
            issueDateEnd: $issueDateEnd
            dueDateStart: $dueDateStart
            dueDateEnd: $dueDateEnd
          )
      }
    `,
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }, fragmentVariables) {
      return { ...fragmentVariables, count, cursor };
    },
  },
);
